import {z} from 'zod';

import {isNil} from 'ramda';
import {isNilOrEmpty} from 'ramda-adjunct';

import i18n from '@dms/i18n';
import {invoiceNameSchema} from '@dms/shared';

import {Nullish} from 'shared/web';

const PRICE_TYPES_WITH_PURCHASE_PRICE = [
  'SVCPRICETYPE_CALCULATION_PRICE',
  'SVCPRICETYPE_DIRECT_PRICE',
] as const;

const PRICE_TYPE_COOPERATION = 'SVCPRICETYPE_COOPERATION';

type PriceType =
  | 'SVCPRICETYPE_TIME_NORM'
  | 'SVCPRICETYPE_CALCULATION_PRICE'
  | 'SVCPRICETYPE_DIRECT_PRICE'
  | 'SVCPRICETYPE_COOPERATION';

// A cleared input holds null, so the type error needs the same message as the empty string.
const requiredString = () => {
  const message = i18n.t('general.validations.fieldIsRequired');

  return z.string({required_error: message, invalid_type_error: message}).min(1, message);
};

export const editWorkItemFormSchema = (priceType: PriceType | Nullish) =>
  z
    .object({
      name: invoiceNameSchema,
      number: requiredString(),
      isPriceUnitManual: z.boolean(),
      vatType: z.string().nullish(),
      quantity: z.number().nullish(),
      purchasePricePerUnit: z.number().nullish(),
      sellingPricePerUnit: z.number().nullish(),
      workType: requiredString(),
      isDoNotApplyDiscount: z.boolean(),
    })
    .superRefine((data, ctx) => {
      if (priceType !== PRICE_TYPE_COOPERATION) {
        if (isNil(data.quantity)) {
          ctx.addIssue({
            path: ['quantity'],
            code: z.ZodIssueCode.custom,
            message: i18n.t('general.validations.fieldIsRequired'),
          });
        } else if (data.quantity < 1) {
          ctx.addIssue({
            path: ['quantity'],
            code: z.ZodIssueCode.custom,
            message: i18n.t('general.validations.minValue', {min: 1}),
          });
        }
      }

      if (data.isPriceUnitManual) {
        if (
          PRICE_TYPES_WITH_PURCHASE_PRICE.includes(
            priceType as (typeof PRICE_TYPES_WITH_PURCHASE_PRICE)[number]
          ) &&
          isNil(data.purchasePricePerUnit)
        ) {
          ctx.addIssue({
            path: ['purchasePricePerUnit'],
            code: z.ZodIssueCode.custom,
            message: i18n.t('general.validations.fieldIsRequired'),
          });
        }

        if (isNil(data.sellingPricePerUnit)) {
          ctx.addIssue({
            path: ['sellingPricePerUnit'],
            code: z.ZodIssueCode.custom,
            message: i18n.t('general.validations.fieldIsRequired'),
          });
        }

        if (isNilOrEmpty(data.vatType)) {
          ctx.addIssue({
            path: ['vatType'],
            code: z.ZodIssueCode.custom,
            message: i18n.t('general.validations.fieldIsRequired'),
          });
        }
      }
    });

export type EditWorkItemFormType = z.infer<ReturnType<typeof editWorkItemFormSchema>>;
